import { useState } from 'react'
import Layout, { Card, Empty } from '@/components/Layout'
import { Link, router } from '@inertiajs/react'
import { useCanWrite } from '@/lib/access'
import type { GoalSummary, TaskFull } from '@/types'

const SUGGEST = [
  { pts: 5, label: '⭐ 轻松 5' },
  { pts: 10, label: '⭐⭐ 一般 10' },
  { pts: 20, label: '⭐⭐⭐ 较难 20' },
  { pts: 50, label: '⭐⭐⭐⭐ 挑战 50' },
]

export default function Show({ goal, tasks }: { goal: GoalSummary; tasks: TaskFull[] }) {
  const canWrite = useCanWrite()
  const [title, setTitle] = useState('')
  const [points, setPoints] = useState('10')
  const [cycle, setCycle] = useState('daily')

  const [editing, setEditing] = useState(false)
  const [eTitle, setETitle] = useState(goal.title)
  const [eWhy, setEWhy] = useState(goal.why ?? '')
  const [eDeadline, setEDeadline] = useState(goal.deadline ?? '')
  const [eTarget, setETarget] = useState(String(goal.target_points || ''))
  const [eStatus, setEStatus] = useState(goal.status)

  const addTask = () => {
    if (!title.trim() || !Number(points)) return
    router.post(`/goals/${goal.id}/tasks`, { title, points, cycle }, {
      onSuccess: () => { setTitle(''); setPoints('10') },
    })
  }

  const startEdit = () => {
    setETitle(goal.title)
    setEWhy(goal.why ?? '')
    setEDeadline(goal.deadline ?? '')
    setETarget(String(goal.target_points || ''))
    setEStatus(goal.status)
    setEditing(true)
  }

  const saveEdit = () => {
    if (!eTitle.trim()) return
    router.patch(`/goals/${goal.id}`, {
      title: eTitle, why: eWhy, deadline: eDeadline,
      target_points: eTarget, status: eStatus,
    }, { onSuccess: () => setEditing(false) })
  }

  const [showExport, setShowExport] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [importText, setImportText] = useState('')
  const [importErr, setImportErr] = useState('')

  const CYCLE_CN: Record<string, string> = { once: '一次性', daily: '每日', weekly: '每周' }
  const exportText = tasks
    .map((t) => `${t.title}||${CYCLE_CN[t.cycle] ?? t.cycle}||+${t.points}`)
    .join('\n')

  const copyExport = async () => {
    try {
      await navigator.clipboard.writeText(exportText)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // 剪贴板不可用（非安全上下文等）：选中文本让用户手动复制
      alert('复制失败，请手动选择文本复制')
    }
  }

  const submitImport = () => {
    setImportErr('')
    router.post(`/goals/${goal.id}/tasks/import`, { text: importText }, {
      onError: (errors) => setImportErr((errors as Record<string, string>).import ?? '导入失败'),
      onSuccess: () => { setShowImport(false); setImportText('') },
    })
  }

  const [editTaskId, setEditTaskId] = useState<number | null>(null)
  const [tTitle, setTTitle] = useState('')
  const [tPoints, setTPoints] = useState('')
  const [tCycle, setTCycle] = useState('daily')

  const startTaskEdit = (t: TaskFull) => {
    setEditTaskId(t.id)
    setTTitle(t.title)
    setTPoints(String(t.points))
    setTCycle(t.cycle)
  }

  const saveTaskEdit = () => {
    if (!tTitle.trim() || !Number(tPoints)) return
    router.patch(`/tasks/${editTaskId}`, { title: tTitle, points: tPoints, cycle: tCycle }, {
      onSuccess: () => setEditTaskId(null),
    })
  }

  return (
    <Layout>
      <Card title={<><Link href="/goals" className="bg-[#eef2ff] text-[#5b7cfa] font-semibold text-[13px] px-3 py-1.5 rounded-[10px] mr-1.5">← 返回</Link> {goal.title} {goal.status === 'done' ? <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#e7f8ee] text-[#2ec16b]">已达成</span> : <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#eef2ff] text-[#5b7cfa]">进行中</span>}</>}
        extra={<span className="flex items-center gap-2">{goal.deadline ? <span className="text-[#707d90] text-[13px]">截止 {goal.deadline}</span> : null}{canWrite && !editing && <button onClick={startEdit} className="bg-[#eef2ff] text-[#5b7cfa] font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">✏️ 编辑</button>}</span>}>
        {editing ? (
          <div className="grid gap-2.5">
            <label className="grid gap-1">
              <span className="text-[13px] text-[#707d90]">目标名称</span>
              <input value={eTitle} onChange={(e) => setETitle(e.target.value)} placeholder="目标名称" className="border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
            </label>
            <label className="grid gap-1">
              <span className="text-[13px] text-[#707d90]">为什么（动机）</span>
              <input value={eWhy} onChange={(e) => setEWhy(e.target.value)} placeholder="选填，提醒自己为什么要做" className="border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
            </label>
            <div className="flex gap-2.5 flex-wrap">
              <label className="grid gap-1">
                <span className="text-[13px] text-[#707d90]">截止日期</span>
                <input type="date" value={eDeadline} onChange={(e) => setEDeadline(e.target.value)} className="border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
              </label>
              <label className="grid gap-1">
                <span className="text-[13px] text-[#707d90]">总分门槛</span>
                <input type="number" value={eTarget} onChange={(e) => setETarget(e.target.value)} placeholder="0 = 不设门槛" min={0} className="w-[120px] border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
              </label>
              <label className="grid gap-1">
                <span className="text-[13px] text-[#707d90]">状态</span>
                <select value={eStatus} onChange={(e) => setEStatus(e.target.value)} className="border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]">
                  <option value="active">进行中</option><option value="done">已达成</option>
                </select>
              </label>
            </div>
            <div className="flex gap-2.5">
              <button onClick={saveEdit} className="bg-[#5b7cfa] text-white font-semibold px-4 py-2 rounded-[10px]">保存</button>
              <button onClick={() => setEditing(false)} className="bg-[#eef2ff] text-[#707d90] font-semibold px-4 py-2 rounded-[10px]">取消</button>
            </div>
          </div>
        ) : <>
        {goal.why && <div className="text-[#707d90] text-[13px] mb-2">💡 {goal.why}</div>}
        {goal.target_points > 0 ? (
          <>
            <div className="h-2.5 bg-[#e7ebf2] rounded-full overflow-hidden my-2.5">
              <div className="h-full rounded-full bg-gradient-to-r from-[#5b7cfa] to-[#8a5bfa]" style={{ width: `${goal.progress_pct}%` }} />
            </div>
            <div className="text-[#707d90] text-[13px]">{goal.earned} / {goal.target_points} 分（{goal.progress_pct}%）· {goal.prediction}</div>
          </>
        ) : <div className="text-[#707d90] text-[13px]">已得 {goal.earned} 分（未设总分门槛）</div>}
        </>}
      </Card>

      <Card title={`📝 步骤（${tasks.length}）`} extra={
        <span className="flex items-center gap-2">
          <button onClick={() => setShowExport(true)} disabled={!tasks.length}
            className="bg-[#eef2ff] text-[#5b7cfa] font-semibold text-[13px] px-3 py-1.5 rounded-[10px] disabled:opacity-40">导出</button>
          {canWrite && <button onClick={() => setShowImport(true)}
            className="bg-[#eef2ff] text-[#5b7cfa] font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">导入</button>}
        </span>
      }>
        {tasks.length ? tasks.map((t) => (
          editTaskId === t.id ? (
            <div key={t.id} className="flex gap-2.5 flex-wrap items-center py-2.5 border-t border-dashed border-[#e7ebf2] first:border-t-0">
              <input value={tTitle} onChange={(e) => setTTitle(e.target.value)} placeholder="步骤名称" className="flex-1 min-w-[130px] border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
              <input type="number" value={tPoints} onChange={(e) => setTPoints(e.target.value)} placeholder="积分" min={1} className="w-[88px] border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
              <select value={tCycle} onChange={(e) => setTCycle(e.target.value)} className="border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]">
                <option value="once">一次性</option><option value="daily">每日</option><option value="weekly">每周</option>
              </select>
              <button onClick={saveTaskEdit} className="bg-[#5b7cfa] text-white font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">保存</button>
              <button onClick={() => setEditTaskId(null)} className="bg-[#eef2ff] text-[#707d90] font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">取消</button>
            </div>
          ) : (
          <div key={t.id} className="flex items-center gap-2.5 py-2.5 border-t border-dashed border-[#e7ebf2] first:border-t-0">
            <span className="text-[#f5a623] text-[13px] whitespace-nowrap">{t.stars}</span>
            <span className="flex-1">{t.title} <span className="text-[11px] px-[7px] py-px rounded-md bg-[#eef2ff] text-[#707d90]">{t.cycle_label}</span> {t.done_count > 0 && <span className="text-[#707d90] text-[13px]">· {t.done_count} 次</span>}</span>
            <span className="font-bold text-[#5b7cfa] whitespace-nowrap">+{t.points}</span>
            {canWrite && <button disabled={!t.available} onClick={() => router.post(`/checkin/${t.id}`)} className="bg-[#2ec16b] text-white font-semibold text-[13px] px-3 py-1.5 rounded-[10px] disabled:opacity-40">{t.available ? '完成' : t.avail_label}</button>}
            {canWrite && <button onClick={() => startTaskEdit(t)} className="text-[#707d90] hover:text-[#5b7cfa] text-base px-1.5">✏️</button>}
            {canWrite && <button onClick={() => router.delete(`/tasks/${t.id}`)} className="text-[#707d90] hover:text-[#ef5350] text-base px-1.5">✕</button>}
          </div>
          )
        )) : <Empty>还没有步骤{canWrite ? '，在下方添加第一个。' : '。'}</Empty>}

        {canWrite && (
          <>
            <div className="flex gap-2.5 flex-wrap items-center mt-3.5">
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="步骤，例如：跑步 3 公里" className="flex-1 min-w-[130px] border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
              <input type="number" value={points} onChange={(e) => setPoints(e.target.value)} placeholder="积分" min={1} className="w-[88px] border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
              <select value={cycle} onChange={(e) => setCycle(e.target.value)} className="border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]">
                <option value="once">一次性</option><option value="daily">每日</option><option value="weekly">每周</option>
              </select>
              <button onClick={addTask} className="bg-[#5b7cfa] text-white font-semibold px-4 py-2 rounded-[10px]">添加步骤</button>
            </div>
            <div className="text-[#707d90] text-[13px] mt-2">难度参考（点一下填入，也可自己输任意分值）：
              {SUGGEST.map((s) => (
                <span key={s.pts} onClick={() => setPoints(String(s.pts))} className="inline-block cursor-pointer text-xs px-2.5 py-0.5 mt-1 mr-1 border border-[#e7ebf2] rounded-full hover:border-[#5b7cfa] hover:text-[#5b7cfa]">{s.label}</span>
              ))}
            </div>
          </>
        )}
      </Card>

      {goal && canWrite && (
        <Card>
          <button onClick={() => { if (confirm('删除该目标及其步骤？')) router.delete(`/goals/${goal.id}`) }} className="bg-[#eef2ff] text-[#ef5350] font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">🗑 删除这个目标</button>
        </Card>
      )}
      {showExport && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShowExport(false)}>
          <div className="bg-white rounded-[14px] p-4 w-full max-w-[520px]" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold mb-2">导出步骤</div>
            <textarea readOnly value={exportText} rows={Math.min(12, Math.max(3, tasks.length))}
              className="w-full border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 font-mono text-[13px] outline-none" />
            <div className="flex gap-2.5 mt-3">
              <button onClick={copyExport} className="bg-[#5b7cfa] text-white font-semibold px-4 py-2 rounded-[10px]">{copied ? '已复制 ✓' : '复制'}</button>
              <button onClick={() => setShowExport(false)} className="bg-[#eef2ff] text-[#707d90] font-semibold px-4 py-2 rounded-[10px]">关闭</button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShowImport(false)}>
          <div className="bg-white rounded-[14px] p-4 w-full max-w-[520px]" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold mb-2">导入步骤</div>
            <div className="text-[#707d90] text-[13px] mb-2">每行一条，格式：<code>标题||周期||+分数</code>，周期为 每日 / 每周 / 一次性。任一行格式错误将整体不导入。</div>
            <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={8}
              placeholder={'洗内裤||每日||+1\n拖地板||每日||+1'}
              className="w-full border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 font-mono text-[13px] outline-none focus:border-[#5b7cfa]" />
            {importErr && <div className="text-[#ef5350] text-[13px] mt-2">{importErr}</div>}
            <div className="flex gap-2.5 mt-3">
              <button onClick={submitImport} disabled={!importText.trim()} className="bg-[#5b7cfa] text-white font-semibold px-4 py-2 rounded-[10px] disabled:opacity-40">确认导入</button>
              <button onClick={() => setShowImport(false)} className="bg-[#eef2ff] text-[#707d90] font-semibold px-4 py-2 rounded-[10px]">取消</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
